import { useEffect, useState } from "react";
import { Download, Share, X, MoreVertical } from "lucide-react";

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;

export function InstallButton() {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(isStandalone()); // hide only when already installed
  const [showHelp, setShowHelp] = useState(false);
  const ios = isIos();

  useEffect(() => {
    if (isStandalone()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden) return null;

  const onClick = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch {
        /* ignore */
      }
      setDeferred(null);
      setHidden(true);
    } else {
      setShowHelp((s) => !s); // no native prompt available — show manual instructions
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="install-app-button"
        onClick={onClick}
        title="Install EAZIKAST as an app on your device"
        className="flex items-center gap-1.5 border border-[#2560C9]/50 text-[#2560C9] hover:bg-[#2560C9]/10 text-[10px] font-mono uppercase tracking-wider px-2 py-1 transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Install</span>
      </button>
      {showHelp && !deferred && (
        <div
          data-testid="install-help"
          className="absolute right-0 top-full mt-1.5 w-72 bg-[#16181A] border border-[#2E3033] shadow-[0_8px_30px_rgba(0,0,0,0.6)] z-50 p-3"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">Install EAZIKAST</span>
            <button data-testid="install-help-close" onClick={() => setShowHelp(false)} className="text-[#6E7787] hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {ios ? (
            <p className="text-xs text-[#A0AABF] leading-relaxed flex items-center flex-wrap gap-1">
              In Safari, tap the <Share className="w-3.5 h-3.5 inline text-[#2560C9]" /> <b className="text-white">Share</b> button, then choose{" "}
              <b className="text-white">Add to Home Screen</b>.
            </p>
          ) : (
            <div className="text-xs text-[#A0AABF] leading-relaxed space-y-1.5">
              <p className="flex items-center flex-wrap gap-1">
                Open your browser menu <MoreVertical className="w-3.5 h-3.5 inline text-[#2560C9]" /> and choose{" "}
                <b className="text-white">Install app</b> (or <b className="text-white">Add to Home screen</b>).
              </p>
              <p className="text-[10px] text-[#6E7787]">
                The one-tap install prompt appears automatically on the deployed app.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
